/* eslint-disable react-refresh/only-export-components */

import classNames from "classnames";
import { useTranslations } from "providers";
import { CSSProperties, ReactElement, ReactNode, useMemo, useState } from "react";

import { LeanFilterDialog } from "./LeanFilterDialog";
import { LeanPaginator } from "./LeanPaginator";
import styles from "./LeanTable.module.scss";
import { LeanTableColumnHeader } from "./LeanTableColumnHeader";
import { SortDirection } from "./SortDirection";

export type FilterFunc<TItem> = (items: TItem[]) => TItem[];

export type SetFilterFunc<TItem> = (filter: FilterFunc<TItem> | undefined) => void;

/** Number will be added as pixels. String will be added as given. */
type ColumnWidth = number | string;

export type SortFunc<TItem> = (items: TItem[], sortDirection?: SortDirection) => TItem[];

export interface IColumn<TItem> {
  /** The key is optional, and can be used in scenario where filtering / sorting information is serialized. */
  key?: string;

  /** If specified, a header row is present in the table and the title is displayed in that header. */
  title?: string;

  titleContent?: ReactNode;

  /** If specified, a header row is present in the table and these components are displayed instead of the title. */
  children?: ReactElement;

  /** If specified, the column has a fixed width. */
  width?: ColumnWidth;

  /** If specified, the column has this minimal width, and will expand if more space is available. */
  minWidth?: ColumnWidth;

  /** Function to retrieve the value to be displayed in the cells of this column. */
  getValue: (item: TItem, index: number) => ReactNode;

  /** If specified, a filter menu is available in the header, and the values returned my this function are the possible filter values. */
  getFilterValues?: GetFilterValuesFunc<TItem>;

  /** If specified, the list can be sorted by clicking on this column's header, using this sorting function. */
  sort?: SortFunc<TItem>;

  /** if true, the column can be removed from the view. */
  optional?: boolean;

  /** If specified, returns a class to be added to the <td> element of the row corresponding to a table item. */
  className?: string;

  /** If specified, returns a class to be added to the <th> element of the row corresponding to a table item. */
  headerClassName?: string;
}

export interface ITableProps<TItem> {
  /** The columns to be displayed in the table. */
  columns: IColumn<TItem>[];

  /** The items to be displayed in the table. They are sorted and filtered within this component if necessary before being displayed. */
  items: TItem[];

  /** If specified, returns a class to be added to the <tr> element of the row corresponding to a table item. */
  getRowClassName?: (item: TItem) => string | undefined;

  /** If specified, defines a key for each row. Otherwise, the index is used as key. */
  getKey?: (item: TItem) => number | string;

  /** If specified, the list is paginated. */
  pageSize?: number;

  /** If true, no horizontal lines separate rows. */
  noHorizontalSeparation?: boolean;

  /** If true, no vertical lines separate columns. */
  noVerticalSeparation?: boolean;

  /** If specified, additional CSS class to be added to the <table> element. */
  className?: string;

  /** If specified, sorting is controlled by this value, otherwise the sorting state is managed internally. */
  sortInfo?: ISortInfo;

  /** If sortInfo is specified, onSortingChanged notifies sorting changes to the parent component. */
  onSortingChanged?: (sortInfo: ISortInfo | undefined) => void;

  /** If specified, filters are controlled by this value, otherwise the filter state is managed internally. */
  filterInfos?: IFilterInfo[];

  /** If filterInfos is specified, onFilterChanged notifies filter changes to the parent component. */
  onFilterChanged?: (filterInfos: IFilterInfo[]) => void;

  /**
   * If onOptionalColumnsShownIndicesChanged is specified: indices of optional columns to be displayed, all columns are displayed if undefined.
   * If onOptionalColumnsShownIndicesChanged is not specified: ignored, optional columns to be displayed are managed internally. */
  optionalColumnsShownIndices?: number[];

  /* If specified, optional columns being displayed are managed internally. Otherwise, they are specified with the optionalColumnsShownIndices prop. */
  onOptionalColumnsShownIndicesChanged?: (optionalColumnsShownIndices: number[] | undefined) => void;

  onRowSelected?: (itemIndex: number) => void;

  emptyTableText?: string;
}

export type GetFilterValuesFunc<TItem> = (items: TItem[]) => string[];

export interface ISortInfo {
  columnIndex: number;
  sortDirection: SortDirection;
}

export interface IFilterInfo {
  columnIndex: number;
  filters: string[];
}

export const LeanTable = <TItem,>(props: ITableProps<TItem>) => {
  const {
    columns,
    items,
    getRowClassName,
    pageSize,
    getKey,
    noHorizontalSeparation,
    noVerticalSeparation,
    className,
    filterInfos,
    onFilterChanged,
    onRowSelected,
  } = props;

  const i18n = useTranslations();
  let { emptyTableText } = props;
  if (!emptyTableText) emptyTableText = i18n.Table_NoRows;

  const [sortInfoUncontrolled, setSortInfoUncontrolled] = useState<ISortInfo | undefined>();
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilterColumnIndex, setOpenFilterColumnIndex] = useState<number | undefined>();
  const [openFilterRect, setOpenFilterRect] = useState<DOMRect | undefined>();
  const [optionalColumnsShownIndicesUncontrolled, setOptionalColumnsShownIndicesUncontrolled] = useState<
    number[] | undefined
  >();

  const sortInfo = props.onSortingChanged ? props.sortInfo : sortInfoUncontrolled;
  const onSortingChanged = props.onSortingChanged || setSortInfoUncontrolled;

  const optionalColumnsShownIndices = props.onOptionalColumnsShownIndicesChanged
    ? props.optionalColumnsShownIndices
    : optionalColumnsShownIndicesUncontrolled;
  const setOptionalColumnsShownIndices =
    props.onOptionalColumnsShownIndicesChanged || setOptionalColumnsShownIndicesUncontrolled;

  const [sortedItems, totalSortedItems] = useMemo(() => {
    let result = (sortInfo && columns[sortInfo.columnIndex].sort?.(items, sortInfo.sortDirection)) || items;

    const totalSortedItems = result.length;
    if (pageSize !== undefined) {
      result = result.slice(pageSize * currentPage, pageSize * (currentPage + 1));
    }

    return [result, totalSortedItems];
  }, [items, columns, pageSize, currentPage, sortInfo]);

  const onFilterSaved = (columnIndex: number, selectedCheckboxes: string[] | undefined) => {
    let newFilters = filterInfos ? [...filterInfos] : undefined;

    if (selectedCheckboxes === undefined) {
      newFilters = newFilters!.filter((x) => x.columnIndex !== columnIndex);
    } else {
      const index = filterInfos!.findIndex((x) => x.columnIndex === columnIndex);
      if (index === -1) {
        newFilters?.push({ columnIndex, filters: selectedCheckboxes });
      } else {
        newFilters![index].filters = selectedCheckboxes;
      }
    }

    onFilterChanged!(newFilters!);
  };

  const clearFilter = (columnIndex: number) => {
    let newFilters = filterInfos ? [...filterInfos] : undefined;
    newFilters = newFilters!.filter((x) => x.columnIndex !== columnIndex);
    onFilterChanged!(newFilters!);
  };

  const isFilterActive = (columnIndex: number): boolean => {
    return !!filterInfos?.some((x) => x.columnIndex === columnIndex);
  };

  const onHideColumn = (columnIndex: number): void => {
    setOpenFilterColumnIndex(undefined);
    setOptionalColumnsShownIndices(
      optionalColumnsShownIndices
        ? optionalColumnsShownIndices.filter((i) => i !== columnIndex)
        : columns
            .map((column, index) => ({ index, optional: column.optional }))
            .filter((info) => info.optional && info.index !== columnIndex)
            .map((info) => info.index)
    );
  };

  const isColumnVisible = (columnIndex: number, optional: boolean | undefined) =>
    !optional || !optionalColumnsShownIndices || optionalColumnsShownIndices.includes(columnIndex);

  const tableClassName = classNames(
    styles.table,
    "table",
    "sbb-table-bs",
    "table-hover",
    "table-striped",
    noHorizontalSeparation && styles.noHorizontalSeparation,
    noVerticalSeparation && styles.noVerticalSeparation,
    className
  );

  return (
    <>
      <table className={tableClassName}>
        <thead>
          {columns.some((c) => c.title || c.children || c.sort || c.getFilterValues || c.optional) && (
            <tr>
              {columns.map(
                (column, columnIndex) =>
                  isColumnVisible(columnIndex, column.optional) && (
                    <LeanTableColumnHeader
                      column={column}
                      key={columnIndex}
                      columnIndex={columnIndex}
                      onSortingChanged={column.sort && onSortingChanged}
                      openDialog={
                        column.getFilterValues || column.optional
                          ? (idx, rect) => {
                              setOpenFilterColumnIndex(idx);
                              setOpenFilterRect(rect);
                            }
                          : undefined
                      }
                      isFilterActive={isFilterActive(columnIndex)}
                      sortDirection={sortInfo?.columnIndex === columnIndex ? sortInfo.sortDirection : undefined}
                      onClearFilter={clearFilter}
                    />
                  )
              )}
            </tr>
          )}
        </thead>
        {sortedItems.length === 0 && (
          <tbody>
            <tr>
              <td colSpan={columns.length}>{emptyTableText}</td>
            </tr>
          </tbody>
        )}
        {sortedItems.length > 0 && (
          <tbody>
            {sortedItems.map((item, itemIndex) => (
              <tr
                className={getRowClassName?.(item)}
                key={getKey ? getKey(item) : itemIndex}
                onClick={() => onRowSelected && onRowSelected(itemIndex)}
              >
                {columns.map((column, columnIndex) => {
                  const className = column.className ? classNames(column.className) : undefined;
                  return (
                    isColumnVisible(columnIndex, column.optional) && (
                      <td
                        key={columnIndex}
                        style={getTableCellStyle(column.width, column.minWidth)}
                        className={className}
                      >
                        {column.getValue(item, itemIndex)}
                      </td>
                    )
                  );
                })}
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {pageSize && (
        <LeanPaginator
          pageSize={pageSize}
          length={totalSortedItems}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}
      {openFilterColumnIndex !== undefined && openFilterRect !== undefined && (
        <LeanFilterDialog
          columnIndex={openFilterColumnIndex}
          anchorRect={openFilterRect}
          checkboxes={columns[openFilterColumnIndex].getFilterValues?.(items!)}
          initialCheckboxes={filterInfos!.find((fi) => fi.columnIndex === openFilterColumnIndex)?.filters}
          onHide={columns[openFilterColumnIndex].optional ? () => onHideColumn(openFilterColumnIndex) : undefined}
          onSave={(selectedCheckboxes) => {
            onFilterSaved(openFilterColumnIndex, selectedCheckboxes);
            setOpenFilterColumnIndex(undefined);
            setOpenFilterRect(undefined);
          }}
          onCancel={() => {
            setOpenFilterColumnIndex(undefined);
            setOpenFilterRect(undefined);
          }}
        />
      )}
    </>
  );
};

export const getTableCellStyle = (
  width: ColumnWidth | undefined,
  minWidth: ColumnWidth | undefined
): CSSProperties | undefined => {
  let result: CSSProperties | undefined;

  if (width) {
    result = { ...result, width: typeof width === "number" ? `${width}px` : width };
  }

  if (minWidth) {
    result = { ...result, minWidth: typeof minWidth === "number" ? `${minWidth}px` : minWidth };
  }

  return result;
};
